import React, { useState } from 'react';
import styled from 'styled-components';
import { ModalV2 } from 'components/Modal';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const DemoButton = styled.button`
  background: ${(props) => props.theme.colors.primary};
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e03e0c;
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${(props) => props.theme.colors.text.primary};
  border: 1px solid ${(props) => props.theme.colors.card.border};
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.text.primary};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: ${(props) => props.theme.colors.text.primary};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid ${(props) => props.theme.colors.card.border};
  border-radius: 6px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(250, 70, 22, 0.1)'
          : 'rgba(250, 70, 22, 0.1)'};
  }
`;

const ModalDocs = () => {
  const [basicModalOpen, setBasicModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [largeModalOpen, setLargeModalOpen] = useState(false);
  const [noCloseModalOpen, setNoCloseModalOpen] = useState(false);

  const props = [
    {
      name: 'isOpen',
      type: 'boolean',
      required: true,
      description: 'Controls modal visibility',
    },
    {
      name: 'onClose',
      type: 'function',
      required: true,
      description: 'Callback when modal should close',
    },
    {
      name: 'title',
      type: 'string',
      required: false,
      description: 'Modal title displayed in header',
    },
    {
      name: 'subtitle',
      type: 'string',
      required: false,
      description: 'Modal subtitle/description displayed below title',
    },
    {
      name: 'children',
      type: 'node',
      required: true,
      description: 'Modal content',
    },
    {
      name: 'footer',
      type: 'node',
      required: false,
      description: 'Custom footer content (buttons, actions, etc.)',
    },
    {
      name: 'maxWidth',
      type: 'string',
      required: false,
      description: 'Maximum width of modal (default: "600px")',
    },
    {
      name: 'closable',
      type: 'boolean',
      required: false,
      description: 'Show close button (default: true)',
    },
    {
      name: 'closeOnOverlayClick',
      type: 'boolean',
      required: false,
      description: 'Close modal when clicking overlay (default: true)',
    },
  ];

  return (
    <>
      <PageTitle>Modal</PageTitle>
      <PageDescription>
        A reusable, theme-aware modal component for displaying content in an
        overlay. Includes built-in animations, blur backdrop, keyboard support
        (ESC to close), and mobile-responsive design.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic Modal</SectionTitle>
        <ComponentDemo
          title="Simple modal with title and content"
          code={`import { ModalV2 } from 'components/Modal';

const [isOpen, setIsOpen] = useState(false);

<ModalV2
  isOpen={isOpen}
  onClose={() => setIsOpen(false)}
  title="Welcome"
  subtitle="This is a basic modal example"
>
  <p>This is the modal content. You can put any React components here.</p>
</ModalV2>`}
        >
          <DemoButton onClick={() => setBasicModalOpen(true)}>
            Open Basic Modal
          </DemoButton>

          <ModalV2
            isOpen={basicModalOpen}
            onClose={() => setBasicModalOpen(false)}
            title="Welcome"
            subtitle="This is a basic modal example"
          >
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              This is the modal content. You can put any React components here.
              The modal automatically handles overlay clicks, ESC key press, and
              body scroll locking.
            </p>
          </ModalV2>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Modal with Form</SectionTitle>
        <ComponentDemo
          title="Modal with form fields and custom footer"
          code={`<ModalV2
  isOpen={isOpen}
  onClose={() => setIsOpen(false)}
  title="Create New Item"
  subtitle="Fill in the details below"
  footer={
    <>
      <SecondaryButton onClick={() => setIsOpen(false)}>
        Cancel
      </SecondaryButton>
      <DemoButton onClick={handleSave}>
        Save Changes
      </DemoButton>
    </>
  }
>
  <FormGroup>
    <Label>Name</Label>
    <Input type="text" placeholder="Enter name" />
  </FormGroup>
  <FormGroup>
    <Label>Email</Label>
    <Input type="email" placeholder="Enter email" />
  </FormGroup>
</ModalV2>`}
        >
          <DemoButton onClick={() => setFormModalOpen(true)}>
            Open Form Modal
          </DemoButton>

          <ModalV2
            isOpen={formModalOpen}
            onClose={() => setFormModalOpen(false)}
            title="Create New Item"
            subtitle="Fill in the details below"
            footer={
              <>
                <SecondaryButton onClick={() => setFormModalOpen(false)}>
                  Cancel
                </SecondaryButton>
                <DemoButton onClick={() => setFormModalOpen(false)}>
                  Save Changes
                </DemoButton>
              </>
            }
          >
            <FormGroup>
              <Label>Name</Label>
              <Input type="text" placeholder="Enter name" />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input type="email" placeholder="Enter email" />
            </FormGroup>
          </ModalV2>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Large Modal</SectionTitle>
        <ComponentDemo
          title="Modal with custom maxWidth"
          code={`<ModalV2
  isOpen={isOpen}
  onClose={() => setIsOpen(false)}
  title="Large Modal"
  maxWidth="900px"
>
  <p>This modal has a custom maxWidth of 900px.</p>
</ModalV2>`}
        >
          <DemoButton onClick={() => setLargeModalOpen(true)}>
            Open Large Modal
          </DemoButton>

          <ModalV2
            isOpen={largeModalOpen}
            onClose={() => setLargeModalOpen(false)}
            title="Large Modal"
            subtitle="This modal is wider than the default"
            maxWidth="900px"
          >
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              This modal has a custom maxWidth of 900px, making it suitable for
              displaying more content or wider layouts. You can set any valid
              CSS width value.
            </p>
          </ModalV2>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Non-Closable Modal</SectionTitle>
        <ComponentDemo
          title="Modal without close button"
          code={`<ModalV2
  isOpen={isOpen}
  onClose={() => setIsOpen(false)}
  title="Important Notice"
  closable={false}
  closeOnOverlayClick={false}
  footer={
    <DemoButton onClick={() => setIsOpen(false)}>
      I Understand
    </DemoButton>
  }
>
  <p>This modal requires explicit action to close.</p>
</ModalV2>`}
        >
          <DemoButton onClick={() => setNoCloseModalOpen(true)}>
            Open Non-Closable Modal
          </DemoButton>

          <ModalV2
            isOpen={noCloseModalOpen}
            onClose={() => setNoCloseModalOpen(false)}
            title="Important Notice"
            subtitle="Please read carefully"
            closable={false}
            closeOnOverlayClick={false}
            footer={
              <DemoButton onClick={() => setNoCloseModalOpen(false)}>
                I Understand
              </DemoButton>
            }
          >
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              This modal has no close button and cannot be dismissed by clicking
              the overlay or pressing ESC. It requires explicit action through
              the footer button.
            </p>
          </ModalV2>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock
          language="javascript"
          code={`import { ModalV2 } from 'components/Modal';
// or
import ModalV2 from 'components/Modal';`}
        />
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        {/* eslint-disable jsx-a11y/accessible-emoji */}
        <ul style={{ lineHeight: 1.8 }}>
          <li>🎨 Fully theme-aware (light/dark mode support)</li>
          <li>✨ Smooth animations (fade-in overlay, slide-in modal)</li>
          <li>🌫️ Blur backdrop effect</li>
          <li>⌨️ Keyboard support (ESC to close)</li>
          <li>📱 Mobile-responsive design</li>
          <li>🔒 Body scroll locking when open</li>
          <li>🎯 Click outside to close (optional)</li>
          <li>🚀 Customizable footer with buttons</li>
          <li>📏 Configurable max width</li>
        </ul>
        {/* eslint-enable jsx-a11y/accessible-emoji */}
      </Section>
    </>
  );
};

export default ModalDocs;
